import { Box, Checkbox, FormControlLabel, FormHelperText, Grid, useMediaQuery, useTheme } from '@material-ui/core';
import { ButtonVariant } from '../../button/buttonProps';
import { confirmationSchema } from '../../../store/order/state';
import { flc } from '../../../utils/strings/firstLetterCapitalize';
import { getNavigationNameForPageEnum, getTitleForPageEnum, PageEnum } from '../../../route/page';
import { getUrlWithConditionsAndTerms } from '../../../config/url';
import { insectControlAddressRoute, insectControlPaymentRoute, insectControlServiceRoute } from 'route/routeInsectControl';
import { ServiceType } from 'common/serviceType';
import { TextInputSize } from '../../form/textInput/textInputProps';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import ConfirmData from 'component/confirmData';
import InfoBoxInsectControl from 'component/confirmData/infoBoxInsectControl';
import PageWrapper from 'component/pageWrapper';
import React, { useEffect, useRef } from 'react';
import TextInput from '../../form/textInput';
import useBillingClient from 'store/order/hook/address/useBillingClient/useBillingClient';
import useConfirmationData from '../../../store/order/hook/confirmation/useConfirmationData/useConfirmationData';
import useDeliveryClient from 'store/order/hook/address/useDeliveryClient/useDeliveryClient';
import useInsectControlData from 'store/order/hook/insectControlService/useInsectControlData/useInsectControlData';
import useSetConfirmationDataWasSetAction from '../../../store/order/hook/confirmation/useSetMessageWasSetAction/useSetConfirmationDataWasSetAction';

interface FormInputs {
    readonly agreeTerms: true;
    readonly message: string | null;
}

const InsectControlConfirmationPage: React.FC<{}> = () => {
    const theme = useTheme();
    const isSmallLayout = useMediaQuery(theme.breakpoints.down('sm'));

    const { t } = useTranslation();
    const setConfirmationDataWasSetAction = useSetConfirmationDataWasSetAction();
    const history = useHistory();
    const insectControlData = useInsectControlData();
    const deliveryClient = useDeliveryClient();
    const billingClient = useBillingClient();
    const confirmationData = useConfirmationData();

    const termsRef = useRef<HTMLFormElement>(null);

    const { register, errors, handleSubmit } = useForm<FormInputs>({
        resolver: zodResolver(confirmationSchema),
        defaultValues: {
            message: confirmationData?.message,
        },
    });

    const onSubmit = async (data: FormInputs) => {
        await setConfirmationDataWasSetAction(data);
        history.push(insectControlPaymentRoute.url());
    };

    const termsAndConditionsError = errors.agreeTerms?.type === 'invalid_literal_value';

    useEffect(() => {
        if (termsAndConditionsError === true) {
            // @ts-ignore
            termsRef.current.scrollIntoView();
        }
    }, [termsAndConditionsError]);

    if (deliveryClient === undefined || billingClient === undefined) {
        history.push(insectControlAddressRoute.url());
        return null;
    }
    if (insectControlData === undefined) {
        history.push(insectControlServiceRoute.url());
        return null;
    }

    return (
        <PageWrapper
            page={PageEnum.insectControlConfirm}
            pageTitle={getNavigationNameForPageEnum(PageEnum.insectControlConfirm)}
            serviceType={ServiceType.insectControl}
            wizardItems={[PageEnum.insectControlAddress, PageEnum.insectControlService, PageEnum.insectControlConfirm, PageEnum.insectControlPayment]}
            title={flc(getTitleForPageEnum(PageEnum.insectControlConfirm))}
            subtitle={flc(t('order.screen.confirmation.subtitle'))}
            price={{
                label: flc(t('order.price')),
                amount: insectControlData.price,
            }}
            secondButton={{
                arrowContinue: true,
                onClick: handleSubmit(onSubmit),
                text: t('order.navigation.continue'),
                variant: ButtonVariant.contained,
            }}
            firstButton={{
                onClick: () => {
                    history.push(insectControlServiceRoute.url());
                },
                text: t('order.navigation.back'),
                variant: ButtonVariant.outlined,
            }}
        >
            <ConfirmData deliveryClientData={deliveryClient} billingClientData={billingClient}>
                <InfoBoxInsectControl problem={insectControlData.problem} description={insectControlData.description} />
            </ConfirmData>

            <Box marginTop={isSmallLayout ? 0 : '20px'}>
                <form onSubmit={handleSubmit(onSubmit)} ref={termsRef}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextInput name="message" size={TextInputSize.big} placeholder={flc(t('order.screen.confirmation.form.message.placeholder'))} inputRef={register} rows={3} />
                        </Grid>
                        <Grid item xs={6}>
                            {insectControlData.images.map((file) => {
                                return <img key={file.name} src={URL.createObjectURL(file)} alt={file.name} style={{ height: '100px', marginRight: '14px' }} />;
                            })}
                        </Grid>
                        <Grid item xs={12}>
                            <Box mt={2}>
                                {t('order.screen.confirmation.weWillContactYou')}
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox inputRef={register} name={'agreeTerms'} />}
                                label={
                                    <>
                                        {t('order.screen.confirmation.termAndConditions.label')}&nbsp;
                                        <a href={getUrlWithConditionsAndTerms()} target={'_blank'}>
                                            {t('order.screen.confirmation.termAndConditions.labelLing')}
                                        </a>
                                    </>
                                }
                            />
                            {termsAndConditionsError === true && (
                                <FormHelperText
                                    style={{
                                        color: theme.palette.secondary.main,
                                        marginLeft: 0,
                                        marginTop: 0,
                                    }}
                                >
                                    {t('order.screen.confirmation.termAndConditions.required')}
                                </FormHelperText>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </Box>
            <Box marginTop={'16px'}>&nbsp;</Box>
        </PageWrapper>
    );
};

export default InsectControlConfirmationPage;
